import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getSubmission,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import {
  Checkbox,
  InitialsInput,
  Input,
  PhoneInput,
} from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getSchema } from "./joi/validation";

function COReleaseOfInformationMP() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  //Get Client Info
  useEffect(() => {
    if (!clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await getCareTeams(clientid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setCareTeams(response);
    };

    loadClientInfo();
  }, [clientid]);

  useEffect(() => {
    if (!submissionid) {
      return;
    }

    const loadSubmission = async () => {
      const response = await getSubmission(submissionid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setData(response.Data);
      setFormsyDetails(response.FormsyDetails);

      if (response.Comments) {
        setComments(response.Comments);
      }

      if (response.Signatures) {
        setSignatures(response.Signatures);
      }
    };

    loadSubmission();
  }, [submissionid]);

  const [fromInfo, setFromInfo] = useState("");
  const [fromInitials, setFromInitials] = useState();
  const [fromName, setFromName] = useState();
  const [fromOrganization, setFromOrganization] = useState();
  const [fromStreetAddress, setFromStreetAddress] = useState();
  const [fromCityState, setFromCityState] = useState();
  const [fromPhone, setFromPhone] = useState();
  const [fromFax, setFromFax] = useState();

  const handleFromInfoAdd = (e) => {
    e.preventDefault();

    let fromData = fromInfo;
    if (fromData) fromData += "\n";
    fromData += `Initials: ${fromInitials}, Name: ${fromName}, Organization: ${fromOrganization}, ${fromStreetAddress}, ${fromCityState}, Phone: ${fromPhone}, Fax: ${fromFax}`;
    setFromInfo(fromData);
    const obj = { ...data };
    obj.FromInfo["@value"] = fromData;
    setData(obj);
    setFromInitials("");
    setFromName("");
    setFromOrganization("");
    setFromStreetAddress("");
    setFromCityState("");
    setFromPhone("");
    setFromFax("");
  };

  const customValidate = (errs) => {
    if (
      (!data.Written["@value"] || data.Written["@value"] === "false") &&
      (!data.Verbal["@value"] || data.Verbal["@value"] === "false") &&
      (!data.Fax["@value"] || data.Fax["@value"] === "false") &&
      (!data.Audio["@value"] || data.Audio["@value"] === "false") &&
      (!data.Video["@value"] || data.Video["@value"] === "false") &&
      (!data.Email["@value"] || data.Email["@value"] === "false")
    ) {
      if (errs)
        errs.MethodOfRelease = "At least one Method of Release is required";
      else
        errs = {
          MethodOfRelease: "At least one Method of Release is required",
        };
    }

    setErrors(errs);
    return errs;
  };

  const handleMethodOfReleaseChange = async (e) => {
    const errs = { ...errors };
    delete errs.MethodOfRelease;
    setErrors(errs);
    handleChange(e, errs);
  };

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          customValidate={customValidate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="container">
                <div className="row">
                  <div className="col">
                    <h6 className="text-center mt-4">
                      INFORMATION EXCHANGED/RELEASED TO
                    </h6>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Input
                      name="ToName"
                      label="Name"
                      error={errors.ToName}
                      value={data.ToName["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="ToOrganization"
                      label="Organization"
                      error={errors.ToOrganization}
                      value={data.ToOrganization["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Input
                      name="ToStreetAddress"
                      label="Street Address"
                      error={errors.ToStreetAddress}
                      value={data.ToStreetAddress["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="ToCityState"
                      label="City/State"
                      error={errors.ToCityState}
                      value={data.ToCityState["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <PhoneInput
                      name="ToPhone"
                      label="Phone"
                      error={errors.ToPhone}
                      value={data.ToPhone["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <PhoneInput
                      name="ToFax"
                      label="Fax"
                      error={errors.ToFax}
                      value={data.ToFax["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Input
                      name="ToATTN"
                      label="ATTN"
                      error={errors.ToATTN}
                      value={data.ToATTN["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <h6 className="text-center mt-4">
                      INFORMATION EXCHANGED/RELEASED FROM
                    </h6>
                  </div>
                </div>
                <div className="row mb-4">
                  <div className="col">
                    <InitialsInput
                      name="FromInitials"
                      label="Client Initials"
                      value={fromInitials}
                      onChange={(e) => setFromInitials(e.currentTarget.value)}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Input
                      name="FromName"
                      label="Name"
                      value={fromName}
                      onChange={(e) => setFromName(e.currentTarget.value)}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="FromOrganization"
                      label="Organization"
                      value={fromOrganization}
                      onChange={(e) =>
                        setFromOrganization(e.currentTarget.value)
                      }
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Input
                      name="FromStreetAddress"
                      label="Street Address"
                      value={fromStreetAddress}
                      onChange={(e) =>
                        setFromStreetAddress(e.currentTarget.value)
                      }
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="FromCityState"
                      label="City/State"
                      value={fromCityState}
                      onChange={(e) => setFromCityState(e.currentTarget.value)}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <PhoneInput
                      name="FromPhone"
                      label="Phone"
                      value={fromPhone}
                      onChange={(e) => setFromPhone(e.currentTarget.value)}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <PhoneInput
                      name="FromFax"
                      label="Fax"
                      value={fromFax}
                      onChange={(e) => setFromFax(e.currentTarget.value)}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <button
                      className="btn btn-primary"
                      onClick={handleFromInfoAdd}
                      disabled={
                        !fromInitials ||
                        !fromName ||
                        !fromOrganization ||
                        !fromStreetAddress ||
                        !fromCityState ||
                        !fromPhone
                      }
                    >
                      Add
                    </button>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <div
                      className="p-2 mt-4 mb-4 w-100 h-100"
                      style={{
                        backgroundColor: "white",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {data.FromInfo ? data.FromInfo["@value"] : ""}
                      {errors.FromInfo && (
                        <div className="alert alert-danger">
                          {errors.FromInfo}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="row mt-4">
                  <div className="col">
                    <h6>
                      Specific Type of Information to be Disclosed/Obtained:
                    </h6>
                    <Checkbox
                      name="SocialHistory"
                      label="Social History"
                      checked={data.SocialHistory["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Checkbox
                      name="DiagnosticAssessment"
                      label="Diagnostic Assessment Information"
                      checked={data.DiagnosticAssessment["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Checkbox
                      name="ProgressReports"
                      label="Progress Reports"
                      checked={data.ProgressReports["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Checkbox
                      name="LivingAssessments"
                      label="Independent Living Assessments"
                      checked={data.LivingAssessments["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Checkbox
                      name="DischargeSummary"
                      label="Discharge Summary"
                      checked={data.DischargeSummary["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Checkbox
                      name="TreatmentPlan"
                      label="Treatment Plan"
                      checked={data.TreatmentPlan["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Checkbox
                      name="Medications"
                      label="Medication Records"
                      checked={data.Medications["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Checkbox
                      name="HIVTesting"
                      label="Disclosure of information concerning HIV testing "
                      checked={data.HIVTesting["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Checkbox
                      name="AIDSTesting"
                      label="Disclosure of information concerning AIDS or AIDS-related conditions"
                      checked={data.AIDSTesting["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Checkbox
                      name="SubstanceAbuse"
                      label="Disclosure of information concerning any drug or alcohol abuse"
                      checked={data.SubstanceAbuse["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Checkbox
                      name="PsychConditions"
                      label="Disclosure of information concerning psychiatric/psychological conditions"
                      checked={data.PsychConditions["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Checkbox
                      name="OtherDisclosed"
                      label="Other"
                      checked={data.OtherDisclosed["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.OtherDisclosed["@value"] === "true" && (
                      <Input
                        name="OtherDisclosedDetails"
                        error={errors.OtherDisclosedDetails}
                        value={data.OtherDisclosedDetails["@value"]}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                  <div className="col">
                    <h6>Purpose/Need for Disclosure/Information:</h6>

                    <Checkbox
                      name="Continuity"
                      label="Continuity of Care"
                      checked={data.Continuity["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Checkbox
                      name="TreatmentCompliance"
                      label="Treatment Compliance"
                      checked={data.TreatmentCompliance["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Checkbox
                      name="Employment"
                      label="Employment"
                      checked={data.Employment["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Checkbox
                      name="Legal"
                      label="Legal"
                      checked={data.Legal["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Checkbox
                      name="FamilyInvolvement"
                      label="Family Involvement"
                      checked={data.FamilyInvolvement["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Checkbox
                      name="PhysicianInvolvement"
                      label="Physician Involvement"
                      checked={data.PhysicianInvolvement["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Checkbox
                      name="SchoolInvolvement"
                      label="School Involvement"
                      checked={data.SchoolInvolvement["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Checkbox
                      name="OtherPurpose"
                      label="Other"
                      checked={data.OtherPurpose["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.OtherPurpose["@value"] === "true" && (
                      <Input
                        name="OtherPurposeDetails"
                        error={errors.OtherPurposeDetails}
                        value={data.OtherPurposeDetails["@value"]}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    )}
                    <h6 className="mt-4">
                      Amount of Time for Information to be Disclosed:
                    </h6>
                    <Checkbox
                      name="OneYear"
                      label="One year for child welfare only clients"
                      checked={data.OneYear["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Checkbox
                      name="NinetyDays"
                      label="Ninety days for former providers"
                      checked={data.NinetyDays["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Checkbox
                      name="SixMonths"
                      label="Six Months for mental health clients"
                      checked={data.SixMonths["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Checkbox
                      name="OtherAmount"
                      label="Other (specify)"
                      checked={data.OtherAmount["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.OtherAmount["@value"] === "true" && (
                      <Input
                        name="OtherAmountDetails"
                        error={errors.OtherAmountDetails}
                        value={data.OtherAmountDetails["@value"]}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
                <div className="row">
                  <div className="col-2">Method of Release:</div>
                  <div className="col">
                    <Checkbox
                      name="Written"
                      label="Written"
                      checked={data.Written["@value"] === "true"}
                      onChange={handleMethodOfReleaseChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="Verbal"
                      label="Verbal"
                      checked={data.Verbal["@value"] === "true"}
                      onChange={handleMethodOfReleaseChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="Fax"
                      label="Fax"
                      checked={data.Fax["@value"] === "true"}
                      onChange={handleMethodOfReleaseChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="Audio"
                      label="Audio"
                      checked={data.Audio["@value"] === "true"}
                      onChange={handleMethodOfReleaseChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="Video"
                      label="Video"
                      checked={data.Video["@value"] === "true"}
                      onChange={handleMethodOfReleaseChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="Email"
                      label="Email"
                      checked={data.Email["@value"] === "true"}
                      onChange={handleMethodOfReleaseChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                {errors.MethodOfRelease && (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.MethodOfRelease}
                      </div>
                    </div>
                  </div>
                )}
                <div className="row">
                  <div className="col">
                    <i>
                      I hereby authorize the release and/or exchange of the
                      above identifying information from my records. I hereby
                      release Specialized Alternatives for Families and Youth
                      (SAFY) from all legal responsibility or liability that may
                      arise from this authorization. This consent is subject to
                      revocation by me at any time, except to the extent that
                      the program which is to make this disclosure has already
                      taken action in reliance upon it. SAFY may not condition
                      treatment of me on whether or not I revoke this release of
                      information. This release will remain in effect for 6
                      months for Mental Health Services and one year for all
                      other services from the date of my signature.
                    </i>
                  </div>
                </div>
                <div className="row mt-4">
                  <div className="col">
                    <Checkbox
                      name="ReleaseInEffect"
                      label="I understand that this release will remain in effect and cannot be revoked by me until there has been a formal and effective termination or revocation of my release from confinement, probation, or parole, or other proceeding under which I was mandated into treatment."
                      checked={data.ReleaseInEffect["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <strong>
                      By signing this release, I attest that there are no legal
                      restrictions or court orders preventing the release of the
                      requested information.
                    </strong>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <i>
                      NOTICE: Although HIPAA regulation state that information
                      disclosed may be subject to redisclosure by persons
                      receiving it and is not protected by the federal privacy
                      regulation, this information{" "}
                      <u>
                        <b>is protected</b>
                      </u>{" "}
                      by Federal confidentiality rules (42 CFR Part 2) which
                      state “This information has been disclosed to you from
                      records protected by federal confidentiality rules. The
                      federal rules prohibit you from making any further
                      disclosure unless further disclosure is expressly
                      permitted by the written consent of the person whom it
                      pertains, or as otherwise permitted by 42 CFR Part 2. A
                      general authorization for release of information medical
                      or other information is NOT sufficient for the purpose.
                      The Federal rules restrict any use of this information to
                      criminally investigate or prosecute any alcohol or drug
                      abuse client.”
                    </i>
                  </div>
                </div>
                <div className="row mt-4">
                  <div className="col">Notice of Cancellation:</div>
                  <div className="col">
                    <Input
                      name="CancellationDate"
                      label="Date:"
                      error={errors.CancellationDate}
                      value={data.CancellationDate["@value"]}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="CancellationTime"
                      label="Time:"
                      error={errors.CancellationTime}
                      value={data.CancellationTime["@value"]}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Input
                      name="CancellationMode"
                      label="Mode of Cancellation (telephone, letter, etc.):"
                      error={errors.CancellationMode}
                      value={data.CancellationMode["@value"]}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default COReleaseOfInformationMP;
